/**
 * Cache Debug Data Scripts
 *
 * Displays cached Python scripts for a workflow node in the superadmin
 * cache debug panel. Shows script label, table name, code, and creation date.
 *
 * @module FlowDesigner/runs/cache-debug/CacheDebugDataScripts
 */
import React, { useState } from 'react';
import { Code, CaretDown, CaretRight, Table } from '@phosphor-icons/react';
import { Text } from '@chatsdet/ui';
import { CopyablePre } from '../../../../components/CopyButton';

interface CachedDataScript {
  label: string;
  code: string;
  tableName: string;
  tableRef?: string;
  createdAt: string;
}

export function CacheDebugDataScripts({ scripts }: { scripts: CachedDataScript[] }) {
  if (scripts.length === 0) return null;

  return (
    <div className="space-y-2">
      <Text size="sm" weight="medium" className="text-muted-foreground">
        Cached Data Scripts ({scripts.length})
      </Text>
      {scripts.map((script) => (
        <DataScriptCard key={script.label} script={script} />
      ))}
    </div>
  );
}

function DataScriptCard({ script }: { script: CachedDataScript }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded-md p-2 bg-card">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left cursor-pointer"
      >
        {expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
        <Code size={14} weight="bold" className="text-violet-600 dark:text-violet-400" />
        <Text size="sm" weight="medium" className="flex-1 truncate">
          {script.label}
        </Text>
        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
          <Table size={10} />
          {script.tableName}
        </span>
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <span>Table: {script.tableName}</span>
            {script.tableRef && <span>Ref: {script.tableRef}</span>}
            <span>Created: {new Date(script.createdAt).toLocaleString()}</span>
          </div>
          <CopyablePre value={script.code} label="script">
            <pre className="text-[11px] leading-tight bg-gray-50 dark:bg-gray-800 rounded p-3 overflow-x-auto max-h-64 text-gray-700 dark:text-gray-300 font-mono">
              {script.code}
            </pre>
          </CopyablePre>
        </div>
      )}
    </div>
  );
}
